export const SIDEBAR_OPEN_CLASS = 'fxp-sidebar-open';
export const SIDEBAR_MINI_CLASS = 'fxp-sidebar-mini';

const SIDEBAR_STATE_KEY = 'sidebar_state';
// At/above this width the sidebar is a desktop rail (mini/open, preference honored).
// Below it is a tablet/mobile drawer: forced mini, preference ignored, dimming backdrop.
export const DESKTOP_MIN_WIDTH = 1200;

// localStorage-persisted open/mini preference: survives Turbo navigations, reloads and browser
// restarts. Client-only — the server cannot read it, so it renders the default open state and the
// _sidebar_boot_state primer re-applies a saved mini preference before paint (no flash).
export function getSidebarState() {
  try {
    const stored = window.localStorage.getItem(SIDEBAR_STATE_KEY);
    return stored === 'open' || stored === 'mini' ? stored : null;
  } catch { return null; }
}

export function setSidebarState(state) {
  try {
    window.localStorage.setItem(SIDEBAR_STATE_KEY, state);
  } catch { /* storage unavailable (private mode / disabled) — preference just won't persist */ }
}

// Pure decision: below the tablet breakpoint the drawer is forced mini; from there up the
// cookie wins at every width, falling back to the server-rendered page state on first visit.
export function resolveSidebarState({ width, storedState, pageIsOpen }) {
  if (width < DESKTOP_MIN_WIDTH) return 'mini';
  return storedState || (pageIsOpen ? 'open' : 'mini');
}

// Environment adapter: feeds the live viewport width, cookie and page class into the
// pure decision. Keeps $(window).width() so breakpoint behavior is unchanged.
// When #page is absent (WP-delegated metier shell), defaults to open
export function permanentSidebarState() {
  const $page = $('#page');
  return resolveSidebarState({
    width: $(window).width(),
    storedState: getSidebarState(),
    pageIsOpen: $page.length === 0 || $page.hasClass('page--lateral-menu-open'),
  });
}

export function applyPermanentState(element) {
  if (!element) return;
  const mini = permanentSidebarState() === 'mini';
  element.classList.toggle(SIDEBAR_MINI_CLASS, mini);
  element.classList.toggle(SIDEBAR_OPEN_CLASS, !mini);
}

// Marks a sidebar transiently expanded by a click ("peek") rather than by the
// persisted preference, so mouseleave knows not to collapse it back.
const SIDEBAR_PEEK_ATTR = 'data-transient-open';

// Mini-mode "peek": transiently expand the rail (open class + flag) on a touch tap without writing
// the cookie, so no-hover users can browse; dismissed by backdrop tap or nav. The backdrop (only
// present <1200, from construction) follows the open class via CSS — peek doesn't manage it.
export function openSidebarPeek(element) {
  if (!element) return;
  element.classList.add(SIDEBAR_OPEN_CLASS);
  element.classList.remove(SIDEBAR_MINI_CLASS);
  element.setAttribute(SIDEBAR_PEEK_ATTR, 'true');
}

// Clear the peek flag only; the caller settles open/mini via applyPermanentState, and the backdrop
// hides itself through the `~ .fxp-sidebar-open` rule once the open class is dropped.
export function dismissSidebarPeek(element) {
  if (!element) return;
  element.removeAttribute(SIDEBAR_PEEK_ATTR);
}

export function isSidebarPeeking(element) {
  return !!element && element.getAttribute(SIDEBAR_PEEK_ATTR) === 'true';
}

// Collapsible sidebars (opt-in) follow the persisted mini/open preference; the rest default to
// locked open on desktop, falling back to the server page state and a dimmed mobile drawer.
export function resolveSidebarLayout({ collapsible, width, storedState, pageIsOpen }) {
  if (collapsible) {
    return {
      retracted: resolveSidebarState({ width, storedState, pageIsOpen }) === 'mini',
      // Never locked: `locked` makes the plugin stamp `fxp-sidebar-force-open`, whose `close()`
      // early-return would make the collapse chevron inert (TW-2323).
      locked: false,
      showObfuscator: width < DESKTOP_MIN_WIDTH,
    };
  }

  const retracted = width < DESKTOP_MIN_WIDTH ? true : !pageIsOpen;

  return {
    retracted,
    locked: !retracted,
    showObfuscator: retracted && width < DESKTOP_MIN_WIDTH,
  };
}
